import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  loadCandidatePortal,
  patchPortalProgress,
  submitPortalFormAnswers,
  finalSubmitPortal,
  uploadPortalFormFile,
} from '../../../Redux/ActionCreators/onboardingAction';
import { fetchTenantBranding } from '../../../Redux/ActionCreators/qualifyQuestionsAction';
import './TestFlowPortal.scss';

const getEmbedUrl = (url) => {
  if (!url) return null;
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return url;
};

const getFieldInputType = (type) => {
  const t = (type || '').toLowerCase();
  if (t.includes('long') || t === 'textarea') return 'textarea';
  if (t.includes('select') || t.includes('dropdown')) return 'select';
  if (t.includes('checkbox')) return 'checkbox';
  if (t.includes('file')) return 'file';
  if (t.includes('number')) return 'number';
  if (t.includes('date')) return 'date';
  return 'text';
};

// ─── Step Pills ───────────────────────────────────────────────────────────────
const StepPills = ({ steps, currentStep }) => (
  <div className="cp-step-pills">
    {steps.map((s, i) => (
      <React.Fragment key={s}>
        <div className={`cp-pill ${i === currentStep ? 'active' : ''} ${i < currentStep ? 'done' : ''}`}>
          <span className="cp-pill__num">{i < currentStep ? '✓' : i + 1}</span>
          <span className="cp-pill__label">{s}</span>
        </div>
        {i < steps.length - 1 && <div className={`cp-pill-connector ${i < currentStep ? 'done' : ''}`} />}
      </React.Fragment>
    ))}
  </div>
);

// ─── Progress bar ─────────────────────────────────────────────────────────────
const PortalProgress = ({ current, total, forceComplete }) => {
  const pct = forceComplete ? 100 : (total > 0 ? Math.round((current / total) * 100) : 0);
  return (
    <div className="cp-progress">
      <div className="cp-progress__bar">
        <div className="cp-progress__fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="cp-progress__label">{pct}% complete</span>
    </div>
  );
};

// ─── Welcome Step ─────────────────────────────────────────────────────────────
const WelcomeSection = ({ run, welcome = {}, forms, documents, onNext }) => {
  const embedUrl = welcome.video_url ? getEmbedUrl(welcome.video_url) : null;
  const stepItems = [
    run?.step_flags?.forms && forms.length > 0 && { icon: '📝', label: 'Fill out required forms' },
    run?.step_flags?.documents && documents.length > 0 && { icon: '📄', label: 'Review your documents' },
  ].filter(Boolean);

  return (
    <div className="cp-section">
      <div className="cp-welcome">
        <div className="cp-welcome__card">
          <div className="cp-welcome__avatar">🏢</div>
          <h2 className="cp-welcome__title">
            {welcome.title || `Welcome, ${run?.candidate_name || 'there'}!`}
          </h2>
          {welcome.message ? (
            <div
              className="cp-welcome__msg cp-welcome__msg--html"
              dangerouslySetInnerHTML={{ __html: welcome.message }}
            />
          ) : (
            <p className="cp-welcome__msg">
              We're thrilled to have you join the team. Please complete the steps below to finish your onboarding.
            </p>
          )}
          {embedUrl && (
            <div className="cp-welcome__video">
              <iframe
                src={embedUrl}
                title="Welcome Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
        </div>
        {stepItems.length > 0 && (
          <div className="cp-welcome__steps">
            {stepItems.map((s, i) => (
              <div key={i} className="cp-welcome__step-item">
                <span>{s.icon}</span>
                <span>{s.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="cp-section__footer">
        <button className="cp-btn cp-btn--primary" onClick={onNext}>
          Get Started
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// ─── Single Form View ─────────────────────────────────────────────────────────
const FormDetail = ({ form, formIdx, totalForms, answers, setAnswers, onSave, onBack, saving, onFileUpload }) => {
  const formKey = form.id ?? form.name ?? formIdx;
  const [fieldErrors, setFieldErrors] = useState({});

  const setAnswer = (fieldKey, val) => {
    setFieldErrors(prev => ({ ...prev, [fieldKey]: '' }));
    setAnswers(prev => ({
      ...prev,
      [formKey]: { ...(prev[formKey] || {}), [fieldKey]: val },
    }));
  };

  const validateAndSave = (form, formKey) => {
    const currentAnswers = answers[formKey] || {};
    const errors = {};
    (form.fields || []).forEach((field, fieldIdx) => {
      if (!field.required) return;
      const fieldKey = field.id ?? `${field.label ?? ''}_${fieldIdx}`;
      const val = currentAnswers[fieldKey];
      const inputType = getFieldInputType(field.type);
      let isEmpty = false;
      if (inputType === 'checkbox') {
        isEmpty = !val;
      } else if (inputType === 'file') {
        isEmpty = !val;
      } else {
        isEmpty = val === undefined || val === null || String(val).trim() === '';
      }
      if (isEmpty) errors[fieldKey] = `${field.label || 'This field'} is required.`;
    });
    setFieldErrors(errors);
    if (Object.keys(errors).length === 0) onSave(form, formKey);
  };

  return (
    <div className="cp-section">
      <div className="cp-form-detail-header">
        <button className="cp-btn cp-btn--sm" onClick={onBack}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to Forms
        </button>
        <span className="cp-form-detail-counter">Form {formIdx + 1} of {totalForms}</span>
      </div>

      <h2 className="cp-section__title" style={{ marginTop: 16 }}>{form.name}</h2>
      {form.description && <p className="cp-section__sub">{form.description}</p>}

      <div className="cp-form-card__fields">
        {(form.fields || []).map((field, fieldIdx) => {
          const fieldKey = field.id ?? `${field.label ?? ''}_${fieldIdx}`;
          const inputType = getFieldInputType(field.type);
          const currentVal = (answers[formKey] || {})[fieldKey];
          return (
            <div key={fieldKey} className="cp-field">
              <label className="cp-field__label">
                {field.label}
                {field.required && <span className="cp-required">*</span>}
              </label>
              {inputType === 'textarea' ? (
                <textarea
                  className="cp-field__input"
                  rows={3}
                  required={field.required}
                  value={currentVal || ''}
                  onChange={e => setAnswer(fieldKey, e.target.value)}
                />
              ) : inputType === 'select' ? (
                <select
                  className="cp-field__input"
                  required={field.required}
                  value={currentVal || ''}
                  onChange={e => setAnswer(fieldKey, e.target.value)}
                >
                  <option value="">Select…</option>
                  {(Array.isArray(field.options) ? field.options : (field.options || '').split(',')).map(o => String(o).trim()).filter(Boolean).map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              ) : inputType === 'checkbox' ? (
                <label className="cp-field__checkbox">
                  <input
                    type="checkbox"
                    required={field.required}
                    checked={!!currentVal}
                    onChange={e => setAnswer(fieldKey, e.target.checked)}
                  />
                  <span>{field.label}</span>
                </label>
              ) : inputType === 'file' ? (
                <div>
                  <input
                    type="file"
                    className="cp-field__input"
                    accept=".pdf,.docx,.csv,image/*"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const fd = new FormData();
                      fd.append('file', file);
                      const result = await onFileUpload(form.id, fieldIdx, fd);
                      if (result) setAnswer(fieldKey, result);
                      e.target.value = '';
                    }}
                  />
                  <span className="cp-field__file-hint">Accepted formats: PDF, DOCX, CSV, or any image (JPG, PNG, etc.)</span>
                  {currentVal && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#22c55e', marginTop: 4 }}>
                      <span>✓ {typeof currentVal === 'object' && currentVal?.file_name ? currentVal.file_name : 'File uploaded'}</span>
                      {typeof currentVal === 'object' && (currentVal?.file_url || currentVal?.url) && (
                        <a
                          href={currentVal.file_url || currentVal.url}
                          download={currentVal.file_name || true}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#22c55e', lineHeight: 1, flexShrink: 0 }}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                          </svg>
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <input
                  className="cp-field__input"
                  type={inputType}
                  required={field.required}
                  value={typeof currentVal === 'object' ? '' : (currentVal || '')}
                  onChange={e => setAnswer(fieldKey, e.target.value)}
                />
              )}
              {fieldErrors[fieldKey] && (
                <span className="cp-field__error">{fieldErrors[fieldKey]}</span>
              )}
            </div>
          );
        })}
      </div>

      <div className="cp-section__footer">
        <button className="cp-btn" onClick={onBack}>Cancel</button>
        <button className="cp-btn cp-btn--primary" onClick={() => validateAndSave(form, formKey)} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
};

// ─── Forms Step ───────────────────────────────────────────────────────────────
const FormsSection = ({ forms, formAnswers, setFormAnswers, formsSubmitted, onFormSave, onNext, onBack, saving, onFileUpload }) => {
  const [activeFormIdx, setActiveFormIdx] = useState(-1);

  const submittedCount = Object.keys(formsSubmitted).length;
  const allSubmitted = submittedCount === forms.length;

  if (activeFormIdx >= 0) {
    const form = forms[activeFormIdx];
    return (
      <FormDetail
        form={form}
        formIdx={activeFormIdx}
        totalForms={forms.length}
        answers={formAnswers}
        setAnswers={setFormAnswers}
        onSave={async (f, formKey) => {
          await onFormSave(f, formKey);
          setActiveFormIdx(-1);
        }}
        onBack={() => setActiveFormIdx(-1)}
        saving={saving}
        onFileUpload={onFileUpload}
      />
    );
  }

  return (
    <div className="cp-section">
      <div className="cp-section-header">
        <h2 className="cp-section__title">Complete Your Forms</h2>
        <span className="cp-forms-counter">{submittedCount} of {forms.length} submitted</span>
      </div>
      <p className="cp-section__sub">Please fill out and submit all required forms before continuing.</p>

      <div className="cp-forms-list">
        {forms.map((form, idx) => {
          const formKey = form.id ?? form.name ?? idx;
          const isSubmitted = !!formsSubmitted[form.id ?? formKey];
          const hasAnswers = !!(formAnswers[formKey] && Object.keys(formAnswers[formKey]).length > 0);
          return (
            <div key={formKey} className={`cp-form-row ${isSubmitted ? 'submitted' : ''}`}>
              <div className="cp-form-row__number" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: '50%', background: '#f3f4f6', fontWeight: 600, fontSize: 18 }}>
                {idx + 1}
              </div>
              <div className="cp-form-row__info">
                <div className="cp-form-row__name">{form.name}</div>
                <div className="cp-form-row__meta">{(form.fields || []).length} fields</div>
              </div>
              <div className="cp-form-row__status">
                {isSubmitted ? (
                  <div className="cp-status-badge cp-status-badge--done">✓ Submitted</div>
                ) : hasAnswers ? (
                  <div className="cp-status-badge cp-status-badge--progress">In Progress</div>
                ) : (
                  <div className="cp-status-badge cp-status-badge--pending">Pending</div>
                )}
              </div>
              <div className="cp-form-row__action">
                <button
                  className={`cp-btn ${isSubmitted ? 'cp-btn--sm' : 'cp-btn--primary cp-btn--sm'}`}
                  onClick={() => setActiveFormIdx(idx)}
                >
                  {isSubmitted ? 'Edit' : hasAnswers ? 'Continue' : 'Start'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="cp-section__footer">
        <button className="cp-btn" onClick={onBack}>Back</button>
        <button className="cp-btn cp-btn--primary" onClick={onNext} disabled={saving || !allSubmitted}>
          {saving ? 'Saving…' : 'Continue to Next Step'}
        </button>
      </div>
    </div>
  );
};

// ─── Document Viewer Modal ────────────────────────────────────────────────────
const DocViewerModal = ({ doc, isAccepted, onAccept, onClose }) => {
  const [hasOpenedLink, setHasOpenedLink] = React.useState(false);
  const fileUrl = doc.file_url || doc.url;
  const needsAccept = !!doc.requires_accept;
  const mimeType = doc.content_type || doc.mimeType || '';
  const ext = (doc.name || '').split('.').pop().toLowerCase();

  const isImage = ["png", "jpg", "jpeg", "gif", "webp", "bmp"].includes(ext);
  const isPdf = mimeType.includes('pdf') || ext === 'pdf';
  const isDocx = mimeType.includes('word') || ext === 'docx' || ext === 'doc';
  const hasInlinePreview = isImage || isPdf || isDocx;
  const needsOpenFirst = needsAccept && fileUrl && !hasInlinePreview && !hasOpenedLink;

  return (
    <div className="cp-modal-overlay" onClick={onClose}>
      <div className="cp-modal" onClick={e => e.stopPropagation()}>
        <div className="cp-modal__header">
          <span className="cp-modal__title">{doc.name}</span>
          <button className="cp-modal__close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="cp-modal__body">
          {fileUrl ? (
            isImage ? (
              <img src={fileUrl} alt={doc.name} style={{ maxWidth: '100%', maxHeight: '70vh', display: 'block', margin: '0 auto' }} />
            ) : isPdf ? (
              <iframe src={fileUrl} title={doc.name} className="cp-modal__iframe" />
            ) : isDocx ? (
              <iframe src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`} title={doc.name} className="cp-modal__iframe" />
            ) : (
              <div style={{ textAlign: 'center' }}>
                <div className="cp-modal__no-preview">No preview available for this file type.</div>
                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cp-btn"
                  style={{ marginTop: 16, display: 'inline-block' }}
                  onClick={() => setHasOpenedLink(true)}
                >
                  Open File in New Tab
                </a>
              </div>
            )
          ) : (
            <div className="cp-modal__no-preview">No preview available for this file.</div>
          )}
        </div>
        <div className="cp-modal__footer">
          <button className="cp-btn" onClick={onClose}>Close</button>
          {needsAccept && (
            <>
              {needsOpenFirst && (
                <span className="cp-modal__accept-hint">Open the file first to enable acceptance.</span>
              )}
              <button
                className="cp-btn cp-btn--primary"
                onClick={() => { onAccept(doc.id); onClose(); }}
                disabled={isAccepted || needsOpenFirst}
              >
                {isAccepted ? '✓ Accepted' : 'Accept Document'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Documents Step ───────────────────────────────────────────────────────────
const DocumentsSection = ({ documents, onNext, onBack, accepted, setAccepted, viewed, setViewed }) => {
  const [viewingDoc, setViewingDoc] = React.useState(null);

  const fileIconConfig = (mimeType = '') => {
    if (mimeType.includes('pdf')) return { bg: '#fee2e2', color: '#991b1b', label: 'PDF' };
    if (mimeType.includes('word') || mimeType.includes('doc')) return { bg: '#dbeafe', color: '#1e40af', label: 'DOC' };
    return { bg: '#f3f4f6', color: '#6b7280', label: 'FILE' };
  };

  const acceptDocs = documents.filter(d => !!d.requires_accept);
  const readOnlyDocs = documents.filter(d => !d.requires_accept);
  const allAccepted = acceptDocs.every(d => accepted[d.id]);
  const allRead = readOnlyDocs.every(d => viewed[d.id]);
  const canContinue = allAccepted && allRead;

  const handleAccept = (docId) => {
    setAccepted(prev => ({ ...prev, [docId]: true }));
  };

  const handleView = (doc) => {
    setViewed(prev => ({ ...prev, [doc.id]: true }));
    setViewingDoc(doc);
  };

  return (
    <div className="cp-section">
      <h2 className="cp-section__title">Review Documents</h2>
      <p className="cp-section__sub">Please review the following documents before proceeding.</p>

      <div className="cp-docs">
        {documents.map(doc => {
          const icon = fileIconConfig(doc.content_type || doc.mimeType || '');
          const needsAccept = !!doc.requires_accept;
          const hasFile = !!(doc.file_url || doc.url);
          return (
            <div key={doc.id} className={`cp-doc-item ${accepted[doc.id] ? 'accepted' : ''}`}>
              <div className="cp-doc-item-row">
                <div className="cp-doc-item__icon" style={{ background: icon.bg, color: icon.color }}>
                  {icon.label}
                </div>
                <div className="cp-doc-item__info">
                  <div className="cp-doc-item__name">{doc.name}</div>
                  {doc.description && <div className="cp-doc-item__desc">{doc.description}</div>}
                  <div className="cp-doc-item__mode">
                    {needsAccept ? (
                      <span className="cp-doc-badge cp-doc-badge--accept">Requires acceptance</span>
                    ) : (
                      <span className="cp-doc-badge cp-doc-badge--read">Read only</span>
                    )}
                  </div>
                </div>
                <div className="cp-doc-item__actions">
                  {accepted[doc.id] && (
                    <span className="cp-doc-accepted-badge">✓ Accepted</span>
                  )}
                  {hasFile && (
                    <button className="cp-btn cp-btn--sm" onClick={() => handleView(doc)}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                      {viewed[doc.id] ? 'Re-view' : 'View'}
                    </button>
                  )}
                </div>
              </div>
              {needsAccept && !accepted[doc.id] && viewed[doc.id] && (
                <div className="cp-doc-accept-row">
                  <label className="cp-field__checkbox">
                    <input
                      type="checkbox"
                      checked={!!accepted[doc.id]}
                      onChange={e => e.target.checked && handleAccept(doc.id)}
                    />
                    <span>I have read and accept this document</span>
                  </label>
                </div>
              )}
              {needsAccept && !accepted[doc.id] && !viewed[doc.id] && (
                <div className="cp-doc-hint">Open the document and accept it to proceed.</div>
              )}
            </div>
          );
        })}
      </div>

      <div className="cp-section__footer">
        <button className="cp-btn" onClick={onBack}>Back</button>
        <button className="cp-btn cp-btn--primary" onClick={onNext} disabled={!canContinue}>
          I've Reviewed All Documents
        </button>
      </div>

      {viewingDoc && (
        <DocViewerModal
          doc={viewingDoc}
          isAccepted={!!accepted[viewingDoc.id]}
          onAccept={handleAccept}
          onClose={() => setViewingDoc(null)}
        />
      )}
    </div>
  );
};

// ─── Test Done Section ────────────────────────────────────────────────────────
const TestDoneSection = ({ onReset, onExit }) => (
  <div className="cp-section cp-done">
    <div className="cp-done__icon">✅</div>
    <h2 className="cp-done__title">Test Preview Complete</h2>
    <p className="cp-done__msg">
      You've reached the end of this flow. This is a test — no real data was submitted to any candidate.
    </p>
    <div className="cp-done__card">
      <div className="cp-done__card-title">What would a candidate see?</div>
      <ul className="cp-done__list">
        <li>Their submission would be sent to HR for review</li>
        <li>They would receive a confirmation email</li>
        <li>HR can request changes or mark the onboarding complete</li>
      </ul>
      <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
        <button className="cp-btn" onClick={onReset} style={{ flex: 1 }}>
          Restart Preview
        </button>
        <button className="cp-btn cp-btn--primary" onClick={onExit} style={{ flex: 1 }}>
          Exit Test
        </button>
      </div>
    </div>
  </div>
);

// ─── Test Flow Portal ─────────────────────────────────────────────────────────
const TestFlowPortal = ({ sessionToken: propToken, flowName: propName, onBack: propOnBack }) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const sessionToken = propToken || searchParams.get('token') || '';
  const flowName = propName || searchParams.get('name') || 'Flow';
  const onBack = propOnBack || (() => window.close());

  const [brandingLoading, setBrandingLoading] = useState(false);
  const tenantBranding = useSelector((state) => state.qualifyQuestions?.tenantBranding);
  const isWhiteLabel = Boolean(tenantBranding?.is_white_label);

  useEffect(() => {
    if (!sessionToken) return;
    setBrandingLoading(true);
    dispatch(fetchTenantBranding(sessionToken)).finally(() => setBrandingLoading(false));
  }, [sessionToken, dispatch]);

  useEffect(() => {
    if (isWhiteLabel && tenantBranding?.colour) {
      document.documentElement.style.setProperty('--qualify-accent-color', tenantBranding.colour);
    }
  }, [isWhiteLabel, tenantBranding?.colour]);

  const [phase, setPhase] = useState('loading');
  const [portal, setPortal] = useState(null);
  const [step, setStep] = useState(0);
  const [formAnswers, setFormAnswers] = useState({});
  const [formsSubmitted, setFormsSubmitted] = useState({});
  const [docAccepted, setDocAccepted] = useState({});
  const [docViewed, setDocViewed] = useState({});
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const loadPortal = async () => {
    setPhase('loading');
    const portalRes = await dispatch(loadCandidatePortal(sessionToken));
    if (portalRes?.success) {
      const data = portalRes.data;
      setPortal(data);
      setFormsSubmitted(data?.run?.progress?.formsSubmitted || {});
      const apiResponses = data?.responses || {};
      const initialAnswers = {};
      (data?.forms || []).forEach(form => {
        const formKey = form.id ?? form.name;
        const formResp = apiResponses[form.id] || {};
        initialAnswers[formKey] = {};
        (form.fields || []).forEach((fld, i) => {
          const fieldKey = fld.id ?? `${fld.label ?? ''}_${i}`;
          const label = fld.label || `Field ${i + 1}`;
          if (formResp[label] !== undefined) {
            initialAnswers[formKey][fieldKey] = formResp[label];
          }
        });
      });
      setFormAnswers(initialAnswers);
      setStep(0);
      setDocAccepted({});
      setDocViewed({});
      setPhase('loaded');
    } else {
      setErrorMsg(portalRes?.message || 'Failed to load test onboarding.');
      setPhase('error');
    }
  };

  useEffect(() => { loadPortal(); }, [sessionToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeSteps = (() => {
    if (!portal) return ['Welcome', 'Done'];
    const { run, forms, documents } = portal;
    const steps = ['Welcome'];
    if (run?.step_flags?.forms && (forms || []).length > 0) steps.push('Forms');
    if (run?.step_flags?.documents && (documents || []).length > 0) steps.push('Documents');
    steps.push('Done');
    return steps;
  })();

  const handlePortalFormFileUpload = async (formId, fieldIdx, formData) => {
    const res = await dispatch(uploadPortalFormFile(sessionToken, formId, fieldIdx, formData));
    if (!res?.success) return null;
    return res.data?.data ?? res.data;
  };

  const handleFormSave = async (form, formKey) => {
    setSaving(true);
    const answers = formAnswers[formKey] || {};
    const responses = {};
    (form.fields || []).forEach((fld, i) => {
      const label = fld.label || `Field ${i + 1}`;
      const fieldKey = fld.id ?? `${fld.label ?? ''}_${i}`;
      responses[label] = answers[fieldKey] ?? '';
    });
    const res = await dispatch(submitPortalFormAnswers(sessionToken, form.id, { responses, mark_complete: true }));
    if (res?.success || res?.data) {
      setFormsSubmitted(prev => ({ ...prev, [form.id ?? formKey]: true }));
    }
    setSaving(false);
  };

  const handleNext = async () => {
    const nextStep = step + 1;
    setSaving(true);

    const advancingToDone = nextStep === activeSteps.length - 1;
    if (advancingToDone) {
      const docsProgress = {};
      (portal?.documents || []).forEach(doc => {
        const docId = String(doc.id);
        const isViewed = !!(docViewed[docId] || doc.viewed);
        const isAccepted = !!(docAccepted[docId] || doc.accepted);
        if (doc.requires_accept) {
          docsProgress[docId] = { viewed: isViewed, accepted: isAccepted };
        } else {
          docsProgress[docId] = { viewed: isViewed, read: isViewed };
        }
      });
      await dispatch(finalSubmitPortal(sessionToken, {
        progress: {
          idx: nextStep,
          seq: activeSteps.map(s => s.toLowerCase()),
          docs: docsProgress,
          formIdx: 0,
          current_step: step,
          activeFormIdx: -1,
          formResponses: {},
          formsSubmitted: formsSubmitted,
        },
      }));
    } else {
      await dispatch(patchPortalProgress(sessionToken, { progress: { current_step: nextStep } }));
    }

    setStep(nextStep);
    setSaving(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setStep(s => Math.max(0, s - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (brandingLoading || phase === 'loading') {
    return (
      <div className="cp-loading">
        <div className="cp-loading__spinner" />
        <p>Loading test preview…</p>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="cp-error">
        <span className="cp-error__icon">⚠️</span>
        <h2>Failed to Load Test</h2>
        <p>{errorMsg}</p>
        <button className="cp-btn" style={{ marginTop: 16 }} onClick={onBack}>
          Back to Flows
        </button>
      </div>
    );
  }

  const { run, forms, documents } = portal;
  const currentStepName = activeSteps[step];

  return (
    <div className="cp-root">
      {/* Test mode banner */}
      <div className="tfp-test-banner">
        <div className="tfp-test-banner__left">
          <span className="tfp-test-banner__badge">TEST MODE</span>
          <span className="tfp-test-banner__label">
            Previewing flow: <strong>{flowName}</strong> — no data will be sent to any real candidate
          </span>
        </div>
        <button className="tfp-test-banner__exit" onClick={onBack}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
          Exit Test
        </button>
      </div>

      <StepPills steps={activeSteps} currentStep={step} />

      <div className="cp-body">
        <div className="cp-sidebar">
          <div className="cp-sidebar__candidate">
            <div className="cp-sidebar__avatar">
              {(run?.candidate_name || 'T').charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="cp-sidebar__name">{run?.candidate_name || 'Test Candidate'}</div>
              <div className="cp-sidebar__email">{run?.email || 'test@preview.com'}</div>
            </div>
          </div>
          <PortalProgress current={step} total={activeSteps.length - 1} />
          <nav className="cp-sidebar__nav">
            {activeSteps.map((s, i) => (
              <div
                key={s}
                className={`cp-nav-item ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}
              >
                <div className="cp-nav-item__circle">
                  {i < step ? (
                    <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                      <path d="M2 7l3.5 3.5L12 3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    <span>{i + 1}</span>
                  )}
                </div>
                <span>{s}</span>
              </div>
            ))}
          </nav>
        </div>

        <main className="cp-main">
          {currentStepName === 'Welcome' && (
            <WelcomeSection run={run} forms={forms || []} documents={documents || []} onNext={handleNext} />
          )}
          {currentStepName === 'Forms' && (
            <FormsSection
              forms={forms || []}
              formAnswers={formAnswers}
              setFormAnswers={setFormAnswers}
              formsSubmitted={formsSubmitted}
              onFormSave={handleFormSave}
              onNext={handleNext}
              onBack={handleBack}
              saving={saving}
              onFileUpload={handlePortalFormFileUpload}
            />
          )}
          {currentStepName === 'Documents' && (
            <DocumentsSection
              documents={documents || []}
              onNext={handleNext}
              onBack={handleBack}
              accepted={docAccepted}
              setAccepted={setDocAccepted}
              viewed={docViewed}
              setViewed={setDocViewed}
            />
          )}
          {currentStepName === 'Done' && (
            <TestDoneSection
              onReset={() => loadPortal()}
              onExit={onBack}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default TestFlowPortal;
